import React, { useState, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import type { Group, Step, Resource } from './types';
import { parseExcelFile, parseLessonPlanSheet, ParsedLessonPlanRow } from './utils/lmExcelParser';
import { validateLessonPlanRows, rowHasErrors, getRowErrors, ValidationError, RESOURCE_TYPE_MAP } from './utils/lmExcelValidator';
import { generateLessonPlanTemplate } from './utils/lmExcelTemplateGenerator';

const COLORS = ['#2572EB', '#C1004F', '#D39D09', '#107C10', '#5C2E91'];
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

interface ExcelImportModalProps {
  show: boolean;
  onClose: () => void;
  onImport: (groups: Group[], mode: 'replace' | 'append') => void;
  existingGroupCount: number;
}

type ModalStep = 'upload' | 'preview';

/**
 * Convert validated flat rows into the hierarchical Group[] structure.
 */
function buildModuleFromRows(
  validRows: ParsedLessonPlanRow[],
  startIndex: number,
): Group[] {
  const groups: Group[] = [];
  let currentChapterName = '';
  let currentSectionKey = '';
  let currentGroup: Group | null = null;
  let currentStep: Step | null = null;

  for (const row of validRows) {
    const chapterKey = row.chapter;
    const sectionKey = `${row.chapter}|||${row.section}`;

    // New chapter?
    if (chapterKey !== currentChapterName) {
      if (currentStep && currentGroup) currentGroup.steps.push(currentStep);
      if (currentGroup) groups.push(currentGroup);

      const groupIndex = startIndex + groups.length + 1;
      currentGroup = {
        index: groupIndex,
        name: row.chapter,
        color: COLORS[(groupIndex - 1) % COLORS.length],
        steps: [],
      };
      currentChapterName = chapterKey;
      currentSectionKey = '';
      currentStep = null;
    }

    // New section?
    if (sectionKey !== currentSectionKey) {
      if (currentStep && currentGroup) currentGroup.steps.push(currentStep);

      const sectionOrder = (currentGroup?.steps.length || 0) + 1;
      currentStep = {
        name: row.section,
        code: row.sectionCode || slugify(row.section).toUpperCase().replace(/-/g, '_'),
        order: sectionOrder,
        sortOrder: sectionOrder,
        startDate: row.startDate,
        endDate: row.endDate,
        group: row.chapter,
        resources: [],
      };
      currentSectionKey = sectionKey;
    }

    // Add resource if present
    if (row.resourceName && currentStep) {
      const typeKey = (row.resourceType || '').toLowerCase().trim();
      const typeId = RESOURCE_TYPE_MAP[typeKey] || 1;
      const resource: Resource = {
        name: row.resourceName,
        resourceType: 'studyResource',
        studyResourceTypeId: typeId,
        sortOrder: currentStep.resources.length + 1,
      };
      if (row.resourceUrl) {
        resource.url = row.resourceUrl;
      }
      if (typeId === 6 && !row.resourceUrl) {
        // Announcement type — content can be set later
        resource.content = '';
      }
      currentStep.resources.push(resource);
    }
  }

  // Finalize last group/step
  if (currentStep && currentGroup) currentGroup.steps.push(currentStep);
  if (currentGroup) groups.push(currentGroup);

  return groups;
}

/**
 * Compute a summary from parsed rows.
 */
function computeSummary(rows: ParsedLessonPlanRow[]) {
  const chapters = new Set<string>();
  const sections = new Set<string>();
  let resources = 0;

  for (const row of rows) {
    if (row.chapter) chapters.add(row.chapter);
    if (row.chapter && row.section) sections.add(`${row.chapter}|||${row.section}`);
    if (row.resourceName) resources++;
  }

  return { chapters: chapters.size, sections: sections.size, resources };
}

const ExcelImportModal: React.FC<ExcelImportModalProps> = ({
  show,
  onClose,
  onImport,
  existingGroupCount,
}) => {
  const [step, setStep] = useState<ModalStep>('upload');
  const [mode, setMode] = useState<'replace' | 'append'>(existingGroupCount > 0 ? 'append' : 'replace');
  const [rows, setRows] = useState<ParsedLessonPlanRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validRows = rows.filter((_, i) => !rowHasErrors(validationErrors, i));
  const summary = computeSummary(validRows);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = parseExcelFile(buffer);
      const parsedRows = parseLessonPlanSheet(workbook);

      if (parsedRows.length === 0) {
        setParseError('No data rows found. Make sure the first sheet has column headers (Chapter, Section, etc.).');
        return;
      }

      const errors = validateLessonPlanRows(parsedRows);
      setRows(parsedRows);
      setValidationErrors(errors);
      setParseError(null);
      setStep('preview');
    } catch (err: any) {
      setParseError(`Failed to parse Excel file: ${err.message}`);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = () => {
    const startIdx = mode === 'append' ? existingGroupCount : 0;
    const groups = buildModuleFromRows(validRows, startIdx);
    onImport(groups, mode);
  };

  const handleClose = () => {
    setStep('upload');
    setRows([]);
    setValidationErrors([]);
    setParseError(null);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Lesson Plan from Excel</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {parseError && (
          <div className="alert alert-danger">{parseError}</div>
        )}

        {/* Upload Step */}
        {step === 'upload' && (
          <div>
            <div className="mb-3">
              <label className="d-block fw-semibold mb-2">Import mode:</label>
              <div className="d-flex gap-3">
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mode === 'replace'}
                    onChange={() => setMode('replace')}
                  />
                  Replace all chapters
                </label>
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mode === 'append'}
                    onChange={() => setMode('append')}
                  />
                  Append to existing
                </label>
              </div>
              {mode === 'replace' && existingGroupCount > 0 && (
                <div className="text-warning mt-2" style={{ fontSize: 13 }}>
                  Warning: This will replace {existingGroupCount} existing chapter(s) with the imported data.
                </div>
              )}
            </div>

            <div className="mb-3">
              <p className="text-muted" style={{ fontSize: 14 }}>
                Upload an Excel file (.xlsx) with columns: Chapter, Section, Section Code, Start Date, End Date, Resource Name, Resource Type, Resource URL.
              </p>

              <div className="d-flex gap-2">
                <label className="btn btn-primary mb-0" style={{ cursor: 'pointer' }}>
                  Choose Excel File
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </label>

                <Button variant="secondary" onClick={() => generateLessonPlanTemplate()}>
                  Download Template
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && (
          <div>
            <div className="d-flex gap-3 mb-3 align-items-center flex-wrap" style={{ fontSize: 14 }}>
              <span><strong>{rows.length}</strong> rows parsed</span>
              <span style={{ color: '#107C10' }}><strong>{validRows.length}</strong> valid</span>
              {validationErrors.length > 0 && (
                <span style={{ color: 'var(--status-danger)' }}><strong>{rows.length - validRows.length}</strong> with errors</span>
              )}
              <span className="text-muted">
                ({summary.chapters} chapter{summary.chapters !== 1 ? 's' : ''}, {summary.sections} section{summary.sections !== 1 ? 's' : ''}, {summary.resources} resource{summary.resources !== 1 ? 's' : ''})
              </span>
            </div>

            <div className="table-responsive" style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-page)', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>Chapter</th>
                    <th>Section</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Resource</th>
                    <th>Type</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const hasError = rowHasErrors(validationErrors, i);
                    const rowErrors = hasError ? getRowErrors(validationErrors, i) : [];
                    return (
                      <React.Fragment key={i}>
                        <tr style={hasError ? { background: '#fff3f3' } : undefined}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{row.chapter}</td>
                          <td>{row.section}</td>
                          <td>{row.startDate || ''}</td>
                          <td>{row.endDate || ''}</td>
                          <td>{row.resourceName || ''}</td>
                          <td>{row.resourceType || ''}</td>
                          <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.resourceUrl || ''}
                          </td>
                        </tr>
                        {hasError && (
                          <tr>
                            <td colSpan={8} style={{ background: '#fff3f3', padding: '4px 8px', fontSize: 12, color: 'var(--status-danger)' }}>
                              {rowErrors.map((err, ei) => (
                                <div key={ei}>{err.field}: {err.message}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 'upload' && (
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        )}
        {step === 'preview' && (
          <>
            <Button variant="secondary" onClick={() => { setStep('upload'); setRows([]); setValidationErrors([]); }}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={validRows.length === 0}
            >
              Import {validRows.length} row{validRows.length !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExcelImportModal;
